import React, { useCallback, useEffect, useState } from "react";
import { useStoreAuth } from "../StoreAuthContext";
import { toast } from "react-toastify";
import "./StoreSignIn.css";

const REACT_APP_GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function StoreSignIn() {
  const {
    user,
    loading,
    isAuthenticated,
    loginWithGoogle,
    logout,
  } = useStoreAuth();
  const [googleReady, setGoogleReady] = useState(false);

  const handleGoogleSuccess = useCallback(
    (credentialResponse) => {
      const idToken = credentialResponse?.credential;
      if (!idToken) {
        toast.error("Google sign-in did not return a token.");
        return;
      }
      loginWithGoogle(idToken).then((r) => {
        if (r.success) toast.success("Signed in with Google.");
        else toast.error(r.message || "Google sign-in failed.");
      });
    },
    [loginWithGoogle]
  );

  const handleGoogleError = useCallback(() => {
    toast.error("Google sign-in was cancelled or failed.");
  }, []);

  useEffect(() => {
    setGoogleReady(Boolean(REACT_APP_GOOGLE_CLIENT_ID));
  }, []);

  if (loading) {
    return (
      <div className="store-signin store-signin-loading">
        <span>Loading...</span>
      </div>
    );
  }

  if (isAuthenticated && user) {
    return (
      <div className="store-signin store-signin-authenticated">
        <div className="store-signin-user">
          {user.avatar && (
            <img
              src={user.avatar}
              alt=""
              className="store-signin-avatar"
              width={32}
              height={32}
            />
          )}
          <span className="store-signin-name">
            {user.fullName || user.email || "Signed in"}
          </span>
        </div>
        <button
          type="button"
          className="store-signin-logout"
          onClick={logout}
          aria-label="Sign out"
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="store-signin store-signin-buttons">
      {googleReady && (
        <GoogleLoginButton onSuccess={handleGoogleSuccess} onError={handleGoogleError} />
      )}
      {!googleReady && (
        <span className="store-signin-unconfigured">
          Sign-in is not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in your environment.
        </span>
      )}
    </div>
  );
}

// Wrap Google button in provider if the library is available
function GoogleLoginButton({ onSuccess, onError }) {
  const [GoogleButton, setGoogleButton] = useState(null);

  useEffect(() => {
    let mounted = true;
    import("@react-oauth/google")
      .then(({ GoogleOAuthProvider, GoogleLogin }) => {
        if (!mounted || !REACT_APP_GOOGLE_CLIENT_ID) return null;
        return function GoogleBtn(props) {
          return (
            <GoogleOAuthProvider clientId={REACT_APP_GOOGLE_CLIENT_ID}>
              <GoogleLogin
                onSuccess={props.onSuccess}
                onError={props.onError}
                useOneTap={false}
                theme="outline"
                size="large"
                type="standard"
                shape="pill"
                text="signin_with"
                width="250"
                logo_alignment="left"
              />
            </GoogleOAuthProvider>
          );
        };
      })
      .then((Btn) => setGoogleButton(() => Btn))
      .catch(() => setGoogleButton(null));
    return () => { mounted = false; };
  }, []);

  if (!GoogleButton) return null;
  return <GoogleButton onSuccess={onSuccess} onError={onError} />;
}
